import { EXPORT_FORMATS, type ExportFormat, type ExportOptions } from 'src/components/chart/chart-export-modal';

export type LegendItem = { label: string; color: string };
/** Evolution (%) of each visible metric, per bucket; `null` = no value to compare with. */
export type ComparisonValue = { metricKey: string; color: string; value: number | null };

const EXPORT_SCALE = 2;
const PADDING = 48;

const loadImage = (source: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Unable to load ApexCharts PNG'));
    image.src = source;
  });

const hiddenAxis = (axis: any) => ({
  ...axis,
  labels: { ...axis?.labels, show: false },
  axisBorder: { ...axis?.axisBorder, show: false },
  axisTicks: { ...axis?.axisTicks, show: false },
  title: { ...axis?.title, text: undefined },
});

const formatEvolution = (value: number | null): string => {
  if (value === null) return '—';
  if (value > 999) return '>999%';
  if (value < -999) return '<-999%';
  return `${value > 0 ? '+' : ''}${Math.round(value)}%`;
};

const evolutionColor = (value: number | null): string => {
  if (value === null || value === 0) return '#919EAB';
  return value > 0 ? '#22A06B' : '#FF5630';
};

/** Legend drawn top right, right to left. */
function drawLegend(context: CanvasRenderingContext2D, canvas: HTMLCanvasElement, legendItems: LegendItem[]) {
  context.font = `${13 * EXPORT_SCALE}px Public Sans`;
  context.textBaseline = 'middle';
  let legendX = canvas.width - PADDING;
  [...legendItems].reverse().forEach((item) => {
    legendX -= context.measureText(item.label).width;
    context.fillStyle = '#637381';
    context.fillText(item.label, legendX, 160);
    legendX -= 12;
    context.beginPath();
    context.fillStyle = item.color;
    context.arc(legendX, 160, 3, 0, Math.PI * 2);
    context.fill();
    legendX -= 20;
  });
}

/** Evolution values under each x label: a colored dot before the first bucket's values. */
function drawComparison(
  context: CanvasRenderingContext2D,
  chartElement: HTMLElement,
  comparisonValues: ComparisonValue[][],
  image: { x: number; y: number; width: number; height: number }
) {
  const chartRect = chartElement.getBoundingClientRect();
  const xPositions = Array.from(
    chartElement.querySelectorAll<SVGTextElement>('.apexcharts-xaxis-texts-g .apexcharts-xaxis-label')
  ).map((label) => {
    const labelRect = label.getBoundingClientRect();
    const relativeCenterX = (labelRect.left + labelRect.width / 2 - chartRect.left) / chartRect.width;
    return image.x + relativeCenterX * image.width;
  });
  const top = image.y + image.height + 24 * EXPORT_SCALE;
  context.font = `${12 * EXPORT_SCALE}px Public Sans`;
  context.textBaseline = 'middle';
  comparisonValues.forEach((bucketValues, bucketIndex) => {
    const x = xPositions[bucketIndex];
    if (x === undefined) return;
    bucketValues.forEach((metric, metricIndex) => {
      const text = formatEvolution(metric.value);
      const y = top + metricIndex * 18 * EXPORT_SCALE;
      if (bucketIndex === 0) {
        const dotRadius = 4 * EXPORT_SCALE;
        const gap = 6 * EXPORT_SCALE;
        const startX = x - (dotRadius * 2 + gap + context.measureText(text).width) / 2;
        context.beginPath();
        context.fillStyle = metric.color;
        context.arc(startX + dotRadius, y, dotRadius, 0, Math.PI * 2);
        context.fill();
        context.fillStyle = evolutionColor(metric.value);
        context.textAlign = 'left';
        context.fillText(text, startX + dotRadius * 2 + gap, y);
      } else {
        context.fillStyle = evolutionColor(metric.value);
        context.textAlign = 'center';
        context.fillText(text, x, y);
      }
    });
  });
  context.textAlign = 'start';
}

/**
 * PNG export of the Metric evolution chart, as in the Brand Conversions modal: the ApexCharts image (optionally
 * transparent and without axes) centered on a canvas of the chosen format, with the legend and the comparison values.
 * The chart options changed for the capture are always restored.
 */
export async function exportMetricEvolutionChart({
  chartInstance,
  format,
  options,
  legendItems,
  comparisonValues,
}: {
  chartInstance: any;
  format: ExportFormat;
  options: ExportOptions;
  legendItems: LegendItem[];
  comparisonValues: ComparisonValue[][];
}): Promise<boolean> {
  if (!chartInstance || typeof chartInstance.dataURI !== 'function') {
    console.error('ApexCharts instance unavailable', chartInstance);
    return false;
  }
  const selectedFormat = EXPORT_FORMATS.find((item) => item.value === format);
  if (!selectedFormat) {
    console.error('Unknown export format:', format);
    return false;
  }
  const currentConfig = chartInstance.w?.config;
  const originalBackground = currentConfig?.chart?.background;
  const originalXaxis = currentConfig?.xaxis;
  const originalYaxis = currentConfig?.yaxis;
  try {
    await document.fonts.ready;
    const temporaryOptions: any = {
      chart: { background: options.transparent ? 'rgba(0, 0, 0, 0)' : '#FFFFFF' },
    };
    if (options.hideAxes) {
      temporaryOptions.xaxis = hiddenAxis(originalXaxis);
      temporaryOptions.yaxis = (Array.isArray(originalYaxis) ? originalYaxis : [originalYaxis ?? {}]).map((axis: any) => ({
        ...hiddenAxis(axis),
        show: false,
      }));
    }
    await chartInstance.updateOptions(temporaryOptions, false, false, false);
    if (options.transparent) {
      chartInstance.el?.querySelectorAll('.apexcharts-canvas, .apexcharts-svg').forEach((element: HTMLElement) => {
        element.style.background = 'transparent';
      });
    }
    const result = await chartInstance.dataURI({ scale: 2 });
    if (!result?.imgURI) throw new Error('ApexCharts did not return an imgURI');
    const sourceImage = await loadImage(result.imgURI);

    const canvas = document.createElement('canvas');
    canvas.width = selectedFormat.width * EXPORT_SCALE;
    canvas.height = selectedFormat.height * EXPORT_SCALE;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Unable to create export canvas');
    // Transparent export: the canvas is left unfilled
    if (!options.transparent) {
      context.fillStyle = '#FFFFFF';
      context.fillRect(0, 0, canvas.width, canvas.height);
    }
    // The chart keeps its ratio
    const scale = Math.min((canvas.width - PADDING * 2) / sourceImage.width, (canvas.height - PADDING * 2) / sourceImage.height);
    const width = sourceImage.width * scale;
    const height = sourceImage.height * scale;
    const image = { x: (canvas.width - width) / 2, y: (canvas.height - height) / 2, width, height };
    drawLegend(context, canvas, legendItems);
    context.drawImage(sourceImage, image.x, image.y, image.width, image.height);
    if (comparisonValues.length > 0) drawComparison(context, chartInstance.el, comparisonValues, image);

    const variantName = [
      options.transparent ? 'transparent' : 'background',
      options.hideAxes ? 'no-axes' : 'with-axes',
    ].join('-');
    const link = document.createElement('a');
    link.href = canvas.toDataURL('image/png');
    link.download = `metric-evolution-${format}-${variantName}.png`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    return true;
  } catch (error) {
    console.error(`Unable to export ${format} PNG:`, error);
    return false;
  } finally {
    const restoreOptions: any = { chart: { background: originalBackground } };
    if (options.hideAxes) {
      restoreOptions.xaxis = originalXaxis;
      restoreOptions.yaxis = originalYaxis;
    }
    try {
      await chartInstance.updateOptions(restoreOptions, false, false, false);
    } catch (restoreError) {
      console.error('Unable to restore chart options:', restoreError);
    }
  }
}
